import { Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../auth/Auth';
import {
  getReferralLink,
  getReferralList,
  getReferralStats,
  sendReferralCode,
} from '../../../../auth/_requests';
import type { Offer, ReferralStats, referralListResponse } from '../../../../helpers/utils/types';
import { formatDateToDisplayShortMonth, isValidEmail } from '../../../../helpers/utils/utils';
import { showToast } from '../../../../helpers/utils/showToast';
import { usePointTasksContext } from '../../../../helpers/hooks/pointTasksContext';
import {
  getTaskMaxPoints,
  getTaskMaxTickets,
  getTaskPointsText,
  getTaskThresholdText,
  getTaskTicketsText,
} from '../../../../helpers/utils/rewardLabels';
import partyPopperIcon from '../../../../assets/pages/rewards/partyPopper.svg';

const REFERRAL_TIER_NAMES = ['Tier 1', 'Tier 2', 'Tier 3'] as const;

const REFERRAL_TIER_CODES = [
  'referralBonusTier1',
  'referralBonusTier2',
  'referralBonusTier3',
] as const;

const REFERRAL_PAGE_SIZE = 10;

const REF_AVATAR_COLORS = ['#7c3aed', '#059669', '#0891b2', '#dc2626', '#2563eb', '#d97706'];

const REF_HERO_PARTICLES = [
  { tone: 'blue' as const, top: '18%', left: '14%', size: 3, delay: '0s', duration: '6.5s' },
  { tone: 'gold' as const, top: '34%', left: '42%', size: 4, delay: '1.4s', duration: '8s' },
  { tone: 'blue' as const, top: '62%', left: '8%', size: 2, delay: '0.8s', duration: '5.5s' },
  { tone: 'gold' as const, top: '78%', left: '36%', size: 3, delay: '2.6s', duration: '7s' },
  { tone: 'blue' as const, top: '24%', left: '62%', size: 3, delay: '0.4s', duration: '6.8s' },
  { tone: 'gold' as const, top: '70%', left: '74%', size: 4, delay: '3.2s', duration: '7.5s' },
  { tone: 'blue' as const, top: '46%', left: '88%', size: 2, delay: '1.8s', duration: '6s' },
  { tone: 'gold' as const, top: '88%', left: '60%', size: 3, delay: '0.6s', duration: '8.5s' },
];

const TIER_VARIANTS = ['en-ref-tier-blue', 'en-ref-tier-gold', 'en-ref-tier-green'] as const;

const parseTierTarget = (task: Offer) => {
  const fromProgress = Number(task.progress?.totalCount ?? 0);
  if (Number.isFinite(fromProgress) && fromProgress > 0) return fromProgress;

  const fromDescription = task.description?.match(/(\d+)\s+friends?/i)?.[1];
  const parsed = Number(fromDescription ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const initialsFromEmail = (email: string) => {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._\-\s]+/).filter(Boolean);
  if (parts.length >= 2 && parts[0][0] && parts[1][0])
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
};

const isTruthyFlag = (value: unknown) =>
  value === true || value === 1 || String(value ?? '').toLowerCase() === 'true';

const formatSafeDate = (raw?: string | null) => {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '-';
  return formatDateToDisplayShortMonth(raw);
};

export const ReferralsSection = () => {
  const { auth } = useAuth();
  const { getTasksByCode, loading: referralTasksLoading } = usePointTasksContext();
  const [referralLink, setReferralLink] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [stats, setStats] = useState<ReferralStats | undefined>(undefined);
  const [referralList, setReferralList] = useState<referralListResponse['data']>([]);
  const [referralListMeta, setReferralListMeta] = useState<referralListResponse['meta'] | null>(
    null
  );
  const [referralPage, setReferralPage] = useState(1);
  const [referralListLoading, setReferralListLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!auth?.data?.token) return;

      try {
        const res = await getReferralLink(auth.data.token);
        const link =
          typeof res === 'string'
            ? res
            : typeof res?.data === 'string'
              ? res.data
              : typeof res?.referralCode === 'string'
                ? res.referralCode
                : '';
        setReferralLink(link);
      } catch (e) {
        console.error('Error fetching referral code:', e);
        setReferralLink('');
      }

      try {
        const s = await getReferralStats(auth.data.token);
        setStats(s);
      } catch (e) {
        console.error('Error fetching referral stats:', e);
        setStats(undefined);
      }

      setReferralListLoading(true);
      try {
        const listRes = await getReferralList(auth.data.token, {
          page: referralPage,
          limit: REFERRAL_PAGE_SIZE,
        });
        setReferralList(listRes.data ?? []);
        setReferralListMeta(listRes.meta ?? null);
      } catch (e) {
        console.error('Error fetching referral list:', e);
        setReferralList([]);
        setReferralListMeta(null);
      } finally {
        setReferralListLoading(false);
      }
    };
    run();
  }, [auth?.data?.token, referralPage]);

  const referralTasks = useMemo(
    () =>
      getTasksByCode([
        'referralReward',
        'referralBonusTier1',
        'referralBonusTier2',
        'referralBonusTier3',
      ]),
    [getTasksByCode]
  );

  const qualifiedReferrals = Number(stats?.qualifiedReferrals ?? 0);
  const totalList = Number(referralListMeta?.totalCount ?? referralList.length);
  const paidCount = Number(
    stats?.qualifiedReferrals ?? referralList.filter((r) => isTruthyFlag(r.isQualified)).length
  );
  const pendingCount = Math.max(0, totalList - paidCount);

  const monthName = new Date().toLocaleString('en-US', { month: 'long' });
  const referralsThisMonth = useMemo(() => {
    const now = new Date();
    return referralList.filter((r) => {
      const raw = r.invitedOn || r.timestampAdded;
      if (!raw) return false;
      const d = new Date(raw);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [referralList]);

  const referralRewardTask = useMemo(
    () => referralTasks.find((task) => task.code === 'referralReward'),
    [referralTasks]
  );

  const ticketsEarned = qualifiedReferrals * getTaskMaxTickets(referralRewardTask);
  const pointsFromReferralsEst = qualifiedReferrals * getTaskMaxPoints(referralRewardTask);

  const referralRewardTicketsText = getTaskTicketsText(referralRewardTask);
  const referralRewardPointsText = getTaskPointsText(referralRewardTask);
  const referralThresholdText = getTaskThresholdText(referralRewardTask);

  const headlineTickets = referralRewardTicketsText?.replace(/^\+/, '') ?? '';
  const headlinePoints = referralRewardPointsText?.replace(/^\+/, '') ?? '';

  const rowRewardLabel = [referralRewardTicketsText, referralRewardPointsText]
    .filter((s): s is string => Boolean(s))
    .join(' · ');

  const referralTiers = useMemo(
    () =>
      REFERRAL_TIER_CODES.flatMap((code, index) => {
        const task = referralTasks.find((item) => item.code === code);
        if (!task) return [];

        const refs = parseTierTarget(task);
        const progressRaw = Number(task.progress?.completedCount ?? 0);
        const completedCount = Number(task.completedCount ?? 0);
        const progressValue =
          Number.isFinite(progressRaw) && progressRaw > 0 ? progressRaw : completedCount;
        const progress = Math.max(
          0,
          Math.min(refs, Number.isFinite(progressValue) ? progressValue : 0)
        );
        const done = Boolean(task.isCompleted) || progress >= refs || completedCount > 0;

        return [
          {
            name: REFERRAL_TIER_NAMES[index],
            refs,
            progress,
            done,
            pointsText: getTaskPointsText(task)?.replace(/^\+/, '') ?? '',
          },
        ];
      }),
    [referralTasks]
  );

  const copyLink = () => {
    if (!referralLink) return;
    navigator.clipboard?.writeText(referralLink).catch(() => {});
    setReferralCopied(true);
    window.setTimeout(() => setReferralCopied(false), 1800);
  };

  const getStepBadge = (r: referralListResponse['data'][number]) => {
    const qualified = isTruthyFlag(r.isQualified);
    if (qualified) return { className: 'b-converted', label: 'Converted' };
    if (isTruthyFlag(r.isSignedUp)) return { className: 'b-signedup', label: 'Signed up' };
    return { className: 'b-invited', label: 'Invited' };
  };

  return (
    <div className='en-panel en-panel-referrals is-active'>
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50/70 to-white px-4 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-100 text-orange-600">
          <img src={partyPopperIcon} alt="" className="h-7 w-7" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-orange-700">
            Thanksgiving special
          </p>
          <p className="text-sm font-semibold leading-5 text-slate-900">
            We’ve doubled your referral rewards until Nov 10.
          </p>
        </div>

      </div>
      <div className='en-ref-hero'>
        <div className='en-ref-particles' aria-hidden='true'>
          {REF_HERO_PARTICLES.map((p) => (
            <span
              key={`${p.top}-${p.left}`}
              className={`en-ref-particle ${p.tone}`}
              style={{
                top: p.top,
                left: p.left,
                width: p.size,
                height: p.size,
                animationDelay: p.delay,
                animationDuration: p.duration,
              }}
            />
          ))}
        </div>
        <div className='en-ref-hero-eyebrow'>Invite friends</div>
        <h3 className='en-ref-hero-title'>
          Refer a friend.
          {headlineTickets || headlinePoints ? ' Earn ' : null}
          {headlineTickets ? <span className='en-gold'>{headlineTickets}</span> : null}
          {headlineTickets && headlinePoints ? ' + ' : null}
          {headlinePoints ? <span className='en-gold'>{headlinePoints}</span> : null}
        </h3>
        <p className='en-ref-hero-sub'>
          Share your link. When friends make their first payment
          {referralThresholdText ? ` of ${referralThresholdText}+` : ''}, you both get rewarded.
        </p>
        <div className='en-ref-link-row'>
          <input
            className='en-ref-link'
            readOnly
            value={referralLink || '-'}
            aria-label='Referral link'
          />
          <button
            type='button'
            className={`en-ref-copy${referralCopied ? ' is-copied' : ''}`}
            onClick={copyLink}
            disabled={!referralLink}
          >
            {referralCopied ? 'Copied' : 'Copy'}
          </button>
          <button
            type='button'
            className={`en-ref-mail${showEmail ? ' is-active' : ''}`}
            onClick={() => setShowEmail((v) => !v)}
            aria-label='Email invite'
            disabled={!referralLink}
          >
            <Mail size={16} aria-hidden />
          </button>
        </div>
        <div className={`en-ref-email${showEmail ? ' is-open' : ''}`}>
          <input
            className='en-ref-email-input'
            type='email'
            placeholder='friend@example.com'
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              if (!v) setEmailError(null);
              else if (!isValidEmail(v)) setEmailError('Please enter a valid email address');
              else setEmailError(null);
            }}
          />
          <button
            type='button'
            className='en-ref-cancel'
            onClick={() => {
              setShowEmail(false);
              setEmail('');
              setEmailError(null);
            }}
          >
            Cancel
          </button>
          <button
            type='button'
            className='en-ref-send'
            disabled={sending || !email || !!emailError}
            onClick={async () => {
              if (!auth?.data?.token) return;
              if (!email || !isValidEmail(email)) {
                setEmailError('Please enter a valid email address');
                return;
              }
              setSending(true);
              try {
                const res = await sendReferralCode(auth.data.token, email);
                if (res.message === 'success') {
                  showToast({
                    message: 'The invitation has been sent successfully',
                    type: 'success',
                  });
                  setShowEmail(false);
                  setEmail('');
                  setEmailError(null);
                } else if (['already_invited', 'already_registered'].includes(res.message)) {
                  showToast({
                    message: 'This email has already been invited or registered',
                    type: 'error',
                  });
                } else {
                  showToast({ message: 'Failed to send invitation', type: 'error' });
                }
              } catch (e) {
                console.error('Error sending referral code:', e);
                showToast({ message: 'Failed to send invitation', type: 'error' });
              } finally {
                setSending(false);
              }
            }}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {emailError ? (
          <p className='mt-2 font-sans text-[11px] font-semibold text-[#f5a623]'>{emailError}</p>
        ) : null}
      </div>

      <div className='en-ref-tiers-hdr'>
        <div>
          <h4 className='en-ref-tiers-title'>Bonuses</h4>
          <p className='en-ref-tiers-sub'>
            {' '}
            Unlock larger rewards as more friends convert. {monthName} · {referralsThisMonth}{' '}
            referrals so far
          </p>
        </div>
      </div>

      {referralTasksLoading && referralTiers.length === 0 ? (
        <div className='en-empty'>Loading referral tiers…</div>
      ) : (
        <div className='en-ref-tiers'>
          {referralTiers.map((tier, index) => {
            const pct = tier.refs ? Math.round((tier.progress / tier.refs) * 100) : 0;
            const tierVariant = TIER_VARIANTS[index] ?? TIER_VARIANTS[0];

            return (
              <div key={tier.name} className={`en-ref-tier ${tierVariant}`}>
                <span
                  className='en-ref-tier-bubble'
                  style={{ left: '18%', bottom: '10px', animationDelay: '0s' }}
                  aria-hidden='true'
                />
                <div className='en-ref-tier-hdr'>
                  <div className='en-ref-tier-icon' aria-hidden='true'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <polygon points='12 2 14.9 8.5 22 9.3 16.6 14.1 18.2 21 12 17.3 5.8 21 7.4 14.1 2 9.3 9.1 8.5' />
                    </svg>
                  </div>
                  <div className='en-ref-tier-tag'>{tier.name}</div>
                </div>
                <div className='en-ref-tier-reward'>{tier.pointsText}</div>
                {tier.refs > 0 ? (
                  <div className='en-ref-tier-goal'>After {tier.refs} referrals convert</div>
                ) : null}
                <div className='en-ref-tier-progress'>
                  <div className='en-ref-tier-fill' style={{ width: `${pct}%` }} />
                </div>
                <div className='en-ref-tier-meta'>
                  {tier.progress} / {tier.refs}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className='en-ref-history'>
        <div className='en-ref-history-hdr'>
          <h4 className='en-ref-history-title'>Referral history</h4>
          <span className='en-ref-history-sub'>
            {totalList} total · {paidCount} converted · {pendingCount} pending · +{ticketsEarned}{' '}
            tickets · +{pointsFromReferralsEst.toLocaleString('en-US')} pts earned
          </span>
        </div>
        <div className='en-ref-table'>
          <div className='en-ref-th'>
            <span>Friend</span>
            <span>Date</span>
            <span>First payment</span>
            <span>Step</span>
            <span>Reward</span>
          </div>
          {referralListLoading ? (
            <div className='en-empty'>Loading referral history…</div>
          ) : referralList.length === 0 ? (
            <div className='en-empty'>No referrals yet. Share your link to get started.</div>
          ) : (
            referralList.map((r, index) => {
              const dateRaw = r.invitedOn || r.timestampAdded;
              const dateLabel = formatSafeDate(dateRaw);
              const qualified = isTruthyFlag(r.isQualified);
              const firstPaymentLabel = qualified ? formatSafeDate(r.qualifiedOn) : '-';
              const step = getStepBadge(r);
              const displayName = r.email.split('@')[0] ?? r.email;
              const shortName =
                displayName.length > 12 ? `${displayName.slice(0, 10)}…` : displayName;

              return (
                <div key={r.referralId} className='en-ref-tr'>
                  <div className='en-ref-friend'>
                    <div
                      className='en-ref-av'
                      style={{
                        background: REF_AVATAR_COLORS[index % REF_AVATAR_COLORS.length],
                      }}
                    >
                      {initialsFromEmail(r.email)}
                    </div>
                    <span className='en-ref-name' title={r.email}>
                      {shortName}
                    </span>
                  </div>
                  <span className='en-ref-td'>{dateLabel}</span>
                  <span className={`en-ref-td${!qualified ? ' en-ref-muted' : ''}`}>
                    {firstPaymentLabel}
                  </span>
                  <span className='en-ref-td'>
                    <span className={`en-ref-step ${step.className}`}>● {step.label}</span>
                  </span>
                  {qualified ? (
                    <span className='en-ref-reward-pos'>{rowRewardLabel}</span>
                  ) : (
                    <span className='en-ref-reward-muted'>Pending</span>
                  )}
                </div>
              );
            })
          )}
        </div>
        {!referralListLoading && (referralListMeta?.lastPage ?? 1) > 1 && (
          <div className='en-ref-pagination'>
            <button
              type='button'
              className='en-ref-page-btn'
              disabled={!referralListMeta?.hasPreviousPage}
              onClick={() => setReferralPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <div className='en-ref-page-label'>
              Page {referralListMeta?.page ?? 1} of {referralListMeta?.lastPage ?? 1}
            </div>
            <button
              type='button'
              className='en-ref-page-btn'
              disabled={!referralListMeta?.hasNextPage}
              onClick={() =>
                setReferralPage((p) => Math.min(referralListMeta?.lastPage ?? p, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
